import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const [activeFaq, setActiveFaq] = useState<number | null>(0);

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-32 pb-20 text-center px-4">
          <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-3">Artigo não encontrado</h1>
          <p className="text-foreground-600 text-sm mb-6">O artigo que você procura não existe ou foi movido.</p>
          <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white text-sm font-semibold rounded-lg hover:bg-primary-600 transition-colors">
            <i className="ri-home-line w-4 h-4 flex items-center justify-center" />
            Voltar para a Home
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Article Hero */}
      <section className="relative pt-20 md:pt-24 pb-12 md:pb-16 bg-background-100">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-2 mb-4">
              <Link to="/" className="text-xs text-foreground-500 hover:text-primary-600 transition-colors flex items-center gap-1">
                <i className="ri-home-line w-3 h-3 flex items-center justify-center" />
                Home
              </Link>
              <span className="text-foreground-300">/</span>
              <span className="text-xs text-primary-600 font-medium">Guides</span>
              <span className="text-foreground-300">/</span>
              <span className="text-xs text-foreground-500 truncate max-w-[200px]">{article.title}</span>
            </div>
            <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-500 mb-6">
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line w-3 h-3 flex items-center justify-center" />
                {t('published')}: 2026-08-20
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-refresh-line w-3 h-3 flex items-center justify-center" />
                {t('last_updated')}: 2026-08-30
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-user-line w-3 h-3 flex items-center justify-center" />
                {t('author')}: CSSBuyBR Team
              </span>
            </div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary-50 border border-primary-200 rounded-full text-xs text-primary-700">
              <i className="ri-time-line w-3 h-3 flex items-center justify-center" />
              Tempo de leitura: {article.readTime}
            </div>
          </div>
        </div>
      </section>

      <div className="w-full px-4 md:px-6 lg:px-8 py-8 md:py-12">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Sidebar */}
          <aside className="lg:col-span-3">
            <div className="sticky top-24 bg-white rounded-xl border border-background-200 p-5">
              <h3 className="font-semibold text-sm text-foreground-900 mb-4 flex items-center gap-2">
                <i className="ri-list-check w-4 h-4 flex items-center justify-center text-primary-500" />
                {t('table_of_contents')}
              </h3>
              <nav className="space-y-1">
                <a href="#intro" className="block px-3 py-2 text-xs text-foreground-600 hover:text-primary-600 hover:bg-primary-50 rounded-md transition-colors">
                  {t('article_intro')}
                </a>
                {article.sections.map((section, i) => (
                  <a
                    key={i}
                    href={`#section-${i}`}
                    className="block px-3 py-2 text-xs text-foreground-600 hover:text-primary-600 hover:bg-primary-50 rounded-md transition-colors"
                  >
                    {section.heading}
                  </a>
                ))}
                <a href="#faq" className="block px-3 py-2 text-xs text-foreground-600 hover:text-primary-600 hover:bg-primary-50 rounded-md transition-colors">
                  {t('faq')}
                </a>
              </nav>
            </div>
          </aside>

          {/* Main Content */}
          <article className="lg:col-span-9">
            <div className="bg-white rounded-xl border border-background-200 p-6 md:p-8 lg:p-10">
              {/* Hero image */}
              <div className="rounded-lg overflow-hidden mb-10 aspect-[16/9]">
                <img src={article.image} alt={article.title} className="w-full h-full object-cover" />
              </div>

              {/* Introduction */}
              <section id="intro" className="mb-10">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                  {t('article_intro')}
                </h2>
                <div className="prose prose-sm max-w-none text-foreground-600 leading-relaxed space-y-4">
                  {article.intro.map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}
                </div>
              </section>

              {/* Sections */}
              {article.sections.map((section, i) => (
                <section key={i} id={`section-${i}`} className="mb-10">
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                    {section.heading}
                  </h2>
                  <div className="prose prose-sm max-w-none text-foreground-600 leading-relaxed space-y-4">
                    {section.paragraphs.map((p, j) => (
                      <p key={j}>{p}</p>
                    ))}
                    {section.list && (
                      <ul className="space-y-2 list-disc list-inside">
                        {section.list.map((item, j) => (
                          <li key={j}>{item}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </section>
              ))}

              {/* Internal link back to home */}
              <section className="mb-10">
                <div className="p-5 bg-primary-50 border border-primary-200 rounded-xl">
                  <p className="text-sm text-primary-800 font-medium mb-3">Pronto para começar?</p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Link
                      to="/"
                      className="inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-primary-500 text-white text-sm font-semibold rounded-lg hover:bg-primary-600 transition-colors"
                    >
                      <i className="ri-home-line w-4 h-4 flex items-center justify-center" />
                      Voltar para a Página Inicial
                    </Link>
                    <a
                      href="https://kaspreadsheet.com"
                      target="_blank"
                      rel="noopener nofollow"
                      className="inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-white text-foreground-700 border border-background-200 text-sm font-semibold rounded-lg hover:bg-background-50 transition-colors"
                    >
                      Acessar CSSBuy Spreadsheet
                      <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
                    </a>
                  </div>
                </div>
              </section>

              {/* FAQ */}
              <section id="faq" className="mb-10">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
                  {t('faq')}
                </h2>
                <div className="space-y-3">
                  {article.faq.map((item, index) => (
                    <div key={index} className="border border-background-200 rounded-lg overflow-hidden">
                      <button
                        onClick={() => setActiveFaq(activeFaq === index ? null : index)}
                        className="w-full flex items-center justify-between p-4 text-left hover:bg-background-50 transition-colors"
                      >
                        <span className="font-medium text-sm text-foreground-900 pr-4">{item.q}</span>
                        <i className={`${activeFaq === index ? 'ri-subtract-line' : 'ri-add-line'} w-5 h-5 flex items-center justify-center text-foreground-400 shrink-0`} />
                      </button>
                      {activeFaq === index && (
                        <div className="px-4 pb-4">
                          <p className="text-sm text-foreground-600 leading-relaxed">{item.a}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>

              {/* Related articles internal links */}
              <section className="mb-6">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                  {t('related_articles')}
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Link to="/" className="flex items-center gap-3 p-4 bg-background-50 rounded-lg border border-background-200 hover:border-primary-300 hover:bg-primary-50 transition-all">
                    <i className="ri-home-line w-5 h-5 flex items-center justify-center text-primary-500" />
                    <span className="text-sm font-medium text-foreground-700">Página Inicial</span>
                  </Link>
                  {articles
                    .filter((a) => a.slug !== article.slug)
                    .slice(0, 3)
                    .map((a) => (
                      <Link
                        key={a.slug}
                        to={`/article/${a.slug}/`}
                        className="flex items-center gap-3 p-4 bg-background-50 rounded-lg border border-background-200 hover:border-primary-300 hover:bg-primary-50 transition-all"
                      >
                        <i className="ri-file-text-line w-5 h-5 flex items-center justify-center text-primary-500" />
                        <span className="text-sm font-medium text-foreground-700 truncate">{a.title}</span>
                      </Link>
                    ))}
                </div>
              </section>
            </div>
          </article>
        </div>
      </div>

      <Footer />
    </div>
  );
}